import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Button,
  CircularProgress,
  LinearProgress,
  Snackbar,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { AlertTriangle, Network, Plus, RefreshCw, Search } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import kgDatasetService from '../../services/kgDatasetService';
import PageLoader from '../shared/PageLoader';
import sharedStyles from '../PlatformAdmin/PlatformAdmin.module.scss';
import styles from './KnowledgeGraph.module.scss';
import KgCreateDatasetDialog from './KgCreateDatasetDialog';
import {
  INGEST_POLL_MS,
  canActivate,
  describeError,
  failedCount,
  formatDate,
  formatNumber,
  getDatasetStatusMeta,
  indexedCount,
  isIndexing,
  recordCount,
  recordLabel,
  recordNoun,
  unwrapList,
} from './kgUtils';

/**
 * Knowledge Graph dataset list (screen 1).
 *
 * Entry point for the builder. Two things this screen exists to make obvious,
 * because they are the two most common confusions in the feature:
 *
 *   1. A dataset in `draft` is INVISIBLE to agents, however complete it looks.
 *   2. `records` (committed — filters and counts are correct) is not the same as
 *      `indexed` (embedded — semantic search works). While they differ, ingestion
 *      is still running.
 *
 * Lists only datasets the caller owns; there is no "shared with me" endpoint, so
 * a shared dataset is reached through its folder instead.
 */
const KnowledgeGraph = () => {
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();

  const [datasets, setDatasets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [activatingId, setActivatingId] = useState(null);
  const [toast, setToast] = useState({ open: false, message: '', severity: 'info' });

  const pollRef = useRef(null);

  const muiTheme = useMemo(
    () =>
      createTheme({
        palette: {
          mode: isDarkMode ? 'dark' : 'light',
          primary: { main: '#e36250' },
          secondary: { main: '#8b5cf6' },
        },
      }),
    [isDarkMode]
  );

  const notify = useCallback((message, severity = 'info') => {
    setToast({ open: true, message, severity });
  }, []);

  const load = useCallback(async ({ silent = false } = {}) => {
    if (silent) setRefreshing(true);
    else setLoading(true);
    try {
      const data = await kgDatasetService.listDatasets();
      setDatasets(unwrapList(data, 'datasets', 'kg_datasets'));
      setError(null);
    } catch (e) {
      setError(describeError(e, "Couldn't load your knowledge graph datasets."));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Ingestion progress is the ONLY thing that moves on its own — Celery works
  // through the text after a push commits. Poll while any dataset is still
  // embedding, and stop the moment they all catch up. Jobs never need polling:
  // they are complete before they are visible.
  const anyIndexing = useMemo(() => datasets.some((d) => isIndexing(d)), [datasets]);

  useEffect(() => {
    if (!anyIndexing) return undefined;
    pollRef.current = setInterval(() => load({ silent: true }), INGEST_POLL_MS);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = null;
    };
  }, [anyIndexing, load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return datasets;
    return datasets.filter((d) =>
      `${d.label || ''} ${d.key || ''} ${d.description || ''}`.toLowerCase().includes(q)
    );
  }, [datasets, search]);

  const handleCreated = (dataset, handover) => {
    setCreateOpen(false);
    if (!dataset?.id) {
      load({ silent: true });
      return;
    }
    // Straight into the mapping editor: a freshly created dataset has a config
    // inferred from a sample, and reviewing it is the next real step.
    //
    // The sample rides along so the live check is already running when the
    // editor opens. Being asked to choose the file again, seconds after
    // uploading it, is the kind of small insult that makes a tool feel careless.
    navigate(`/knowledge-graph/${dataset.id}?tab=mapping`, {
      state: handover?.records?.length ? { kgSample: handover } : undefined,
    });
  };

  const handleActivate = async (event, dataset) => {
    event.stopPropagation();
    setActivatingId(dataset.id);
    try {
      await kgDatasetService.setStatus(dataset.id, 'active');
      notify(`"${dataset.label}" is live — agents attached to it can query it now.`, 'success');
      await load({ silent: true });
    } catch (e) {
      // 400 here is user-facing and accurate (an empty dataset, most likely).
      notify(describeError(e, "Couldn't activate this dataset."), 'error');
    } finally {
      setActivatingId(null);
    }
  };

  if (loading) {
    return <PageLoader message="Loading knowledge graph datasets..." />;
  }

  const renderCard = (dataset) => {
    const statusMeta = getDatasetStatusMeta(dataset);
    const records = recordCount(dataset);
    const indexed = indexedCount(dataset);
    const failed = failedCount(dataset);
    const indexing = isIndexing(dataset);
    const open = (tab) =>
      navigate(`/knowledge-graph/${dataset.id}${tab ? `?tab=${tab}` : ''}`);

    return (
      <div
        key={dataset.id}
        className={styles.card}
        role="button"
        tabIndex={0}
        onClick={() => open()}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            open();
          }
        }}
      >
        <div className={styles.cardTop}>
          <span className={styles.cardIcon}>
            <Network size={18} />
          </span>
          <Tooltip title={statusMeta.meaning}>
            <span className={`${styles.pill} ${styles[statusMeta.tone]}`}>{statusMeta.label}</span>
          </Tooltip>
        </div>

        <Typography className={styles.cardName}>{dataset.label || 'Untitled dataset'}</Typography>
        <code className={styles.cardKey}>{dataset.key}</code>
        <Typography className={styles.cardDesc}>
          {dataset.description || 'No description'}
        </Typography>

        <div className={styles.cardMeta}>
          <span className={styles.metaStrong}>{recordLabel(dataset, records)}</span>
          {dataset.date_range?.from && (
            <span className={styles.metaItem}>
              {formatDate(dataset.date_range.from)} – {formatDate(dataset.date_range.to)}
            </span>
          )}
        </div>

        {/* "Loaded" and "searchable" are different things, and the gap between
            them is exactly what a user needs to see after a push: filters and
            counts are already right, semantic search is not yet. */}
        {indexing && (
          <div className={styles.progressBlock}>
            <LinearProgress
              variant="determinate"
              value={records ? Math.min(100, (indexed / records) * 100) : 0}
              className={styles.progressBar}
            />
            <span className={styles.progressLabel}>
              Loading text: {formatNumber(indexed)} of {formatNumber(records)} — filters and
              counts already work
            </span>
          </div>
        )}

        {failed > 0 && (
          <button
            type="button"
            className={styles.failedRow}
            onClick={(e) => {
              e.stopPropagation();
              open('history');
            }}
          >
            <AlertTriangle size={13} />
            {formatNumber(failed)} {recordNoun(dataset, failed)} didn’t map — see why
          </button>
        )}

        <div className={styles.cardFooter}>
          {dataset.status === 'draft' ? (
            records > 0 ? (
              // The single most common confusion: everything looks right, and
              // the agent still has no KG tools.
              <div className={styles.draftRow}>
                <span className={styles.draftHint}>Not yet visible to agents</span>
                <Button
                  size="small"
                  variant="contained"
                  className={sharedStyles.primaryBtn}
                  disabled={activatingId === dataset.id || !canActivate(dataset)}
                  onClick={(e) => handleActivate(e, dataset)}
                  startIcon={
                    activatingId === dataset.id ? <CircularProgress size={13} /> : null
                  }
                >
                  {activatingId === dataset.id ? 'Activating…' : 'Activate'}
                </Button>
              </div>
            ) : (
              <div className={styles.draftRow}>
                <span className={styles.draftHint}>No data yet</span>
                <Button
                  size="small"
                  variant="outlined"
                  className={sharedStyles.outlineBtn}
                  onClick={(e) => {
                    e.stopPropagation();
                    open('data');
                  }}
                >
                  Load records
                </Button>
              </div>
            )
          ) : dataset.status === 'paused' ? (
            <span className={sharedStyles.muted}>
              Agents cannot query this — browsing still works
            </span>
          ) : dataset.status === 'archived' ? (
            <span className={sharedStyles.muted}>Retired</span>
          ) : (
            <span className={styles.liveHint}>
              Live · {dataset.facet_count ? `${dataset.facet_count} facets · ` : ''}folder #
              {dataset.folder_id}
            </span>
          )}
        </div>
      </div>
    );
  };

  return (
    <ThemeProvider theme={muiTheme}>
      <div className={sharedStyles.layout}>
        <div className={sharedStyles.inner}>
          <div className={sharedStyles.header}>
            <div className={sharedStyles.titleSection}>
              <h1>Knowledge Graph</h1>
              <p>
                Structured datasets your agents can filter, count and group over exactly — the
                questions vector search answers with a confident guess.
              </p>
            </div>
            <div className={styles.headerActions}>
              <Button
                className={sharedStyles.outlineBtn}
                variant="outlined"
                startIcon={<RefreshCw size={16} />}
                onClick={() => load({ silent: true })}
                disabled={refreshing}
              >
                Refresh
              </Button>
              <Button
                className={sharedStyles.primaryBtn}
                variant="contained"
                startIcon={<Plus size={16} />}
                onClick={() => setCreateOpen(true)}
              >
                New Dataset
              </Button>
            </div>
          </div>

          {error && (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {datasets.length > 0 && (
            <div className={sharedStyles.toolbar}>
              <TextField
                size="small"
                placeholder="Search datasets"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                InputProps={{ startAdornment: <Search size={16} style={{ marginRight: 8 }} /> }}
                sx={{ minWidth: 260 }}
              />
              <span className={sharedStyles.muted}>
                {filtered.length} of {datasets.length} shown
              </span>
            </div>
          )}

          {datasets.length === 0 ? (
            <div className={sharedStyles.emptyState}>
              <Network size={44} className={sharedStyles.emptyIcon} />
              <Typography variant="h6">No datasets yet</Typography>
              <Typography variant="body2" className={sharedStyles.muted}>
                A dataset holds structured records — articles, orders, circulars — with the
                fields worth filtering on declared as facets. Your agents can then answer “how
                many” and “which ones” exactly, instead of ranking by resemblance.
              </Typography>
              <Button
                className={sharedStyles.primaryBtn}
                variant="contained"
                startIcon={<Plus size={16} />}
                onClick={() => setCreateOpen(true)}
                sx={{ mt: 2 }}
              >
                Create your first dataset
              </Button>
            </div>
          ) : filtered.length === 0 ? (
            <div className={sharedStyles.emptyState}>
              <Search size={44} className={sharedStyles.emptyIcon} />
              <Typography variant="h6">No datasets match “{search}”</Typography>
              <Button
                className={sharedStyles.outlineBtn}
                variant="outlined"
                onClick={() => setSearch('')}
                sx={{ mt: 2 }}
              >
                Clear search
              </Button>
            </div>
          ) : (
            <div className={styles.grid}>{filtered.map(renderCard)}</div>
          )}
        </div>
      </div>

      <KgCreateDatasetDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={handleCreated}
        onError={(message) => notify(message, 'error')}
      />

      <Snackbar
        open={toast.open}
        autoHideDuration={5000}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setToast((t) => ({ ...t, open: false }))}
          severity={toast.severity}
          sx={{ width: '100%' }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </ThemeProvider>
  );
};

export default KnowledgeGraph;
